/**
 * ตรรกะของหน้าจอ Outsourcing ที่ไม่ต้องพึ่ง React — แปลง field ของฟอร์มเป็น body
 * ที่ contract รับ, label/tone ของสถานะ, และตัวช่วยตัดสิน "แถวนี้ต้องเรนเดอร์
 * ต่างจากแถวอื่นไหม" (THE SHORTAGE HEADLINE) แยกออกจาก `OutsourceOrderScreen.tsx`
 * เพื่อให้ทดสอบตรรกะล้วน ๆ ได้โดยไม่ต้องเรนเดอร์ DOM — รูปแบบเดียวกับ
 * `lib/packing/packingView.ts`
 */

import { businessDayToIso } from '../businessTime';
import {
  APPROVED_DISCREPANCY_STATUS,
  DISPATCHED_OUTSOURCE_ORDER_STATUS,
  DISPATCH_LOT_QUALITY_HINTS,
  DRAFT_OUTSOURCE_ORDER_STATUS,
  OPEN_DISCREPANCY_STATUS,
  PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS,
  PENDING_MANAGEMENT_APPROVAL_DISCREPANCY_STATUS,
  READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS,
  RETURNED_OUTSOURCE_ORDER_STATUS,
  type DispatchLotQualityHint,
  type OutsourceOrder,
  type OutsourceReturnLine,
} from './types';
import type {
  AddOutsourceOrderLineBody,
  CreateOutsourceOrderBody,
  DispatchOutsourceOrderBody,
  DispatchPackageInput,
  ReceiveOutsourceReturnBody,
  ReturnPackageInput,
} from './types';

/* ------------------------------------------------------------- สถานะใบ --- */

/** ฝาแฝดของ `StatusTone` ใน `desktop/StatusBadge.tsx` — restated แทนการ import
 * ข้ามชั้น `lib/` ไม่พึ่ง `desktop/` (ทิศทางเดียวกับ `lib/packing/packingView.ts`
 * ที่ประกาศ `PackingUnitStatusTone` ของตัวเอง) โครงสร้าง literal union ตรงกัน
 * ทุกตัวอักษร TypeScript จึงยอมรับค่านี้เป็น `tone` ของ `<StatusBadge>` ได้ตรง ๆ */
export type OutsourceStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'planned';

export function outsourceOrderStatusLabel(status: string): string {
  switch (status) {
    case DRAFT_OUTSOURCE_ORDER_STATUS:
      return 'ร่าง';
    case READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS:
      return 'พร้อมส่งออก';
    case DISPATCHED_OUTSOURCE_ORDER_STATUS:
      return 'ส่งออกแล้ว — อยู่กับผู้รับจ้างช่วง';
    case PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS:
      return 'รับกลับบางส่วน';
    case RETURNED_OUTSOURCE_ORDER_STATUS:
      return 'รับกลับครบแล้ว';
    case 'CLOSED':
      return 'ปิดใบแล้ว';
    default:
      return status;
  }
}

export function outsourceOrderStatusTone(status: string): OutsourceStatusTone {
  switch (status) {
    case DRAFT_OUTSOURCE_ORDER_STATUS:
      return 'neutral';
    case READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS:
      return 'info';
    case DISPATCHED_OUTSOURCE_ORDER_STATUS:
      return 'warning';
    case PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS:
      return 'warning';
    case RETURNED_OUTSOURCE_ORDER_STATUS:
      return 'success';
    case 'CLOSED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** ปุ่ม "เพิ่มบรรทัด" เสนอเฉพาะใบที่ยังร่างอยู่ — เพิ่มบรรทัดใบที่ผ่าน ready ไปแล้ว
 * ไม่ได้ (router: บรรทัดปิดพร้อมกับการ ready) */
export function canAddOutsourceOrderLine(order: OutsourceOrder): boolean {
  return order.status === DRAFT_OUTSOURCE_ORDER_STATUS;
}

/** ปุ่ม "ยืนยันพร้อมส่งออก" เสนอเฉพาะใบร่าง — ปุ่มยังคงแสดงแม้ยังไม่มีบรรทัด
 * (เซิร์ฟเวอร์ปฏิเสธจริงด้วย `OUTSOURCE_ORDER_HAS_NO_LINES`) `orderHasNoLines`
 * แยกไว้ต่างหากเพื่อให้หน้าจอใส่เหตุผลกำกับปุ่มที่ปิดอยู่ได้ */
export function canMarkOutsourceOrderReady(order: OutsourceOrder): boolean {
  return order.status === DRAFT_OUTSOURCE_ORDER_STATUS;
}

export function outsourceOrderHasNoLines(order: OutsourceOrder): boolean {
  return order.lines.length === 0;
}

export function canDispatchOutsourceOrder(order: OutsourceOrder): boolean {
  return order.status === READY_TO_DISPATCH_OUTSOURCE_ORDER_STATUS;
}

export function canReceiveOutsourceReturn(order: OutsourceOrder): boolean {
  return (
    order.status === DISPATCHED_OUTSOURCE_ORDER_STATUS ||
    order.status === PARTIALLY_RETURNED_OUTSOURCE_ORDER_STATUS
  );
}

/* -------------------------------------------------------------- ตัวกรอง --- */

export function toOutsourcePlantCodeFilter(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}

export function toOutsourceStatusFilter(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed === '' ? undefined : trimmed;
}

/* --------------------------------------------------------- สร้างใบใหม่ --- */

export interface CreateOutsourceOrderFields {
  readonly plantCode: string;
  readonly supplierCode: string;
  readonly processCode: string;
  /** ค่าจากช่อง `<input type="date">` — `YYYY-MM-DD` */
  readonly expectedReturnDate: string;
  readonly note: string;
}

export const EMPTY_CREATE_OUTSOURCE_ORDER_FIELDS: CreateOutsourceOrderFields = {
  plantCode: '',
  supplierCode: '',
  processCode: '',
  expectedReturnDate: '',
  note: '',
};

/** `null` เมื่อฟอร์มยังกรอกไม่ครบหรือวันที่ไม่ใช่วันจริง — ปุ่มยืนยันปิดอยู่ตราบใด
 * ที่ยังเป็น `null` (นี่คือ client-side validation ล้วน ๆ ไม่ใช่กฎธุรกิจที่ซับซ้อน
 * แบบ D-105 ข้อ 1 ของ Packing — สนามที่ว่างเปล่าไม่มีทางถูกต้องอยู่แล้ว) */
export function toCreateOutsourceOrderBody(
  fields: CreateOutsourceOrderFields,
): CreateOutsourceOrderBody | null {
  const plantCode = fields.plantCode.trim();
  const supplierCode = fields.supplierCode.trim();
  const processCode = fields.processCode.trim();
  const note = fields.note.trim();

  if (plantCode === '' || supplierCode === '' || processCode === '') {
    return null;
  }

  const expectedReturnDate = businessDayToIso(fields.expectedReturnDate);
  if (expectedReturnDate === null) {
    return null;
  }

  return {
    plant_code: plantCode,
    supplier_code: supplierCode,
    process_code: processCode,
    expected_return_date: expectedReturnDate,
    ...(note === '' ? {} : { note }),
  };
}

/* ------------------------------------------------------------- บรรทัด --- */

export interface AddOutsourceOrderLineFields {
  readonly orderNo: string;
  readonly operationSeq: string;
  readonly quantity: string;
  readonly uomCode: string;
}

export const EMPTY_ADD_OUTSOURCE_ORDER_LINE_FIELDS: AddOutsourceOrderLineFields = {
  orderNo: '',
  operationSeq: '',
  quantity: '',
  uomCode: '',
};

function isPositiveDecimalString(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value) && Number(value) > 0;
}

export function toAddOutsourceOrderLineBody(
  fields: AddOutsourceOrderLineFields,
): AddOutsourceOrderLineBody | null {
  const orderNo = fields.orderNo.trim();
  const uomCode = fields.uomCode.trim();
  const quantity = fields.quantity.trim();
  const operationSeqRaw = fields.operationSeq.trim();

  if (orderNo === '' || uomCode === '' || !isPositiveDecimalString(quantity)) {
    return null;
  }
  if (!/^\d+$/.test(operationSeqRaw)) {
    return null;
  }
  const operationSeq = Number(operationSeqRaw);
  if (!Number.isInteger(operationSeq) || operationSeq <= 0) {
    return null;
  }

  return { order_no: orderNo, operation_seq: operationSeq, quantity, uom_code: uomCode };
}

/* -------------------------------------------------------------- dispatch --- */

export interface DispatchPackageDraft {
  readonly lineNo: string;
  readonly lotId: string;
  readonly sourceLocationCode: string;
  readonly quantity: string;
  /** ข้อมูลอ้างอิงของหน้าจอเท่านั้น — ไม่ส่งไปกับ request (ดู `types.ts`) */
  readonly qualityHint: DispatchLotQualityHint;
}

export const EMPTY_DISPATCH_PACKAGE_DRAFT: DispatchPackageDraft = {
  lineNo: '',
  lotId: '',
  sourceLocationCode: '',
  quantity: '',
  qualityHint: '',
};

export function isDispatchLotQualityHint(value: string): value is DispatchLotQualityHint {
  return (DISPATCH_LOT_QUALITY_HINTS as readonly string[]).includes(value);
}

/**
 * **THE QUALITY RULE**: คืนข้อความเตือนเมื่อผู้ใช้บันทึกว่าล็อตนี้ไม่ใช่
 * `RELEASED` — แต่ข้อความนี้เป็น **แค่ป้ายบอก ไม่ใช่ตัวปิดกั้น**: ไม่มีที่ไหนใน
 * `outsourcingView.ts`/`OutsourceOrderScreen.tsx` ที่อ่านค่านี้แล้วปิดปุ่มหรือ
 * ปิดช่องกรอก — invariant ของ Work Order นี้ระบุตรง ๆ ว่า `QUALITY_HOLD`,
 * `BLOCKED` และ `REJECTED` ส่งออกได้เหมือนกันหมด ตรงข้ามกับ Packing ที่ส่งได้
 * เฉพาะ `RELEASED` เท่านั้น
 */
export function dispatchPackageQualityNote(hint: DispatchLotQualityHint): string | null {
  if (hint === '' || hint === 'RELEASED') {
    return null;
  }
  return `ล็อตนี้บันทึกไว้ว่าอยู่ในสถานะ ${hint} — ส่งออกให้ผู้รับจ้างช่วงได้ตามปกติ ไม่ถูกกันเหมือนหน้าจอแพ็กสินค้า เพราะปลายทางคือผู้รับจ้างช่วงที่จะแก้ไขของ ไม่ใช่ลูกค้า`;
}

export function toDispatchPackageInput(draft: DispatchPackageDraft): DispatchPackageInput | null {
  const lineNoRaw = draft.lineNo.trim();
  const lotId = draft.lotId.trim();
  const sourceLocationCode = draft.sourceLocationCode.trim();
  const quantity = draft.quantity.trim();

  if (!/^\d+$/.test(lineNoRaw) || lotId === '' || sourceLocationCode === '') {
    return null;
  }
  if (!isPositiveDecimalString(quantity)) {
    return null;
  }
  const lineNo = Number(lineNoRaw);
  if (!Number.isInteger(lineNo) || lineNo <= 0) {
    return null;
  }

  return { line_no: lineNo, lot_id: lotId, source_location_code: sourceLocationCode, quantity };
}

export function toDispatchOutsourceOrderBody(
  destinationLocationCode: string,
  packageDrafts: readonly DispatchPackageDraft[],
): DispatchOutsourceOrderBody | null {
  const destination = destinationLocationCode.trim();
  if (destination === '' || packageDrafts.length === 0) {
    return null;
  }

  const packages: DispatchPackageInput[] = [];
  for (const draft of packageDrafts) {
    const pkg = toDispatchPackageInput(draft);
    if (pkg === null) {
      return null;
    }
    packages.push(pkg);
  }

  return { destination_location_code: destination, packages };
}

/* ---------------------------------------------------------------- receive --- */

export interface ReturnPackageDraft {
  readonly tagCode: string;
  readonly returnedQuantity: string;
  /** ว่าง = ไม่ระบุส่วนต่าง ("ยังไม่มาส่วนที่เหลือ") */
  readonly varianceQuantity: string;
  readonly varianceReason: string;
}

export const EMPTY_RETURN_PACKAGE_DRAFT: ReturnPackageDraft = {
  tagCode: '',
  returnedQuantity: '',
  varianceQuantity: '',
  varianceReason: '',
};

function isNonNegativeDecimalString(value: string): boolean {
  return /^\d+(\.\d+)?$/.test(value);
}

export function toReturnPackageInput(draft: ReturnPackageDraft): ReturnPackageInput | null {
  const tagCode = draft.tagCode.trim();
  const returnedQuantity = draft.returnedQuantity.trim();
  const varianceQuantity = draft.varianceQuantity.trim();
  const varianceReason = draft.varianceReason.trim();

  if (tagCode === '' || !isNonNegativeDecimalString(returnedQuantity)) {
    return null;
  }
  if (varianceQuantity !== '' && !isNonNegativeDecimalString(varianceQuantity)) {
    return null;
  }

  return {
    tag_code: tagCode,
    returned_quantity: returnedQuantity,
    ...(varianceQuantity === '' ? {} : { variance_quantity: varianceQuantity }),
    ...(varianceReason === '' ? {} : { variance_reason: varianceReason }),
  };
}

export function toReceiveOutsourceReturnBody(
  receivingLocationCode: string,
  packageDrafts: readonly ReturnPackageDraft[],
  note: string,
): ReceiveOutsourceReturnBody | null {
  const receiving = receivingLocationCode.trim();
  const trimmedNote = note.trim();
  if (receiving === '' || packageDrafts.length === 0) {
    return null;
  }

  const packages: ReturnPackageInput[] = [];
  for (const draft of packageDrafts) {
    const pkg = toReturnPackageInput(draft);
    if (pkg === null) {
      return null;
    }
    packages.push(pkg);
  }

  return {
    receiving_location_code: receiving,
    packages,
    ...(trimmedNote === '' ? {} : { note: trimmedNote }),
  };
}

/* ------------------------------------------------------- THE SHORTAGE HEADLINE */

/**
 * **แถวนี้เป็นการรับกลับแบบขาดหรือไม่** — เกณฑ์เดียวคือมี `discrepancy` หรือไม่
 * (schemas.py: "Present exactly when `variance_quantity` is positive") ไม่คำนวณ
 * จาก `variance_quantity` เองซ้ำ เพราะนั่นจะเป็นความจริงชุดที่สองที่ขัดกับ
 * เซิร์ฟเวอร์ได้ — ตัวเลขทุกตัวต้องมาจากสิ่งที่เซิร์ฟเวอร์ตอบมาโดยตรง
 */
export function outsourceReturnLineIsShort(line: OutsourceReturnLine): boolean {
  return line.discrepancy !== null;
}

export function outsourceDiscrepancyIsApprovable(status: string): boolean {
  return status === OPEN_DISCREPANCY_STATUS || status === PENDING_MANAGEMENT_APPROVAL_DISCREPANCY_STATUS;
}

export function outsourceDiscrepancyStatusLabel(status: string): string {
  switch (status) {
    case OPEN_DISCREPANCY_STATUS:
      return 'รอดำเนินการ';
    case PENDING_MANAGEMENT_APPROVAL_DISCREPANCY_STATUS:
      return 'เกิน tolerance — รอผู้จัดการอนุมัติ';
    case APPROVED_DISCREPANCY_STATUS:
      return 'อนุมัติแล้ว';
    default:
      return status;
  }
}

export function outsourceDiscrepancyStatusTone(status: string): OutsourceStatusTone {
  switch (status) {
    case OPEN_DISCREPANCY_STATUS:
      return 'warning';
    case PENDING_MANAGEMENT_APPROVAL_DISCREPANCY_STATUS:
      return 'danger';
    case APPROVED_DISCREPANCY_STATUS:
      return 'success';
    default:
      return 'neutral';
  }
}
